import { FC, useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { RetroCardPayload } from '../../services/signalr/types';

type Props = {
    card: RetroCardPayload;
    contentHidden?: boolean;
    /** Yeni açılan kartta ilk yazım oturumu (blur sonrası kilitlenir). */
    startEditing?: boolean;
    onTextChange: (cardId: string, text: string) => void;
    onMove: (cardId: string, x: number, y: number) => void;
    onDelete: (cardId: string) => void;
    onEditFinished?: (cardId: string) => void;
    surfaceRef: React.RefObject<HTMLElement | null>;
};

function hasWrittenText(value: string | undefined | null): boolean {
    return !!(value || '').trim();
}

export const RetroStickyCard: FC<Props> = ({
    card,
    contentHidden = false,
    startEditing = false,
    onTextChange,
    onMove,
    onDelete,
    onEditFinished,
    surfaceRef,
}) => {
    const { t } = useTranslation();
    const alreadyWritten = hasWrittenText(card.text);
    const [text, setText] = useState(card.text || '');
    const [isWriting, setIsWriting] = useState(() => startEditing && !alreadyWritten);
    const [sealedLocally, setSealedLocally] = useState(alreadyWritten);
    const textareaRef = useRef<HTMLTextAreaElement>(null);
    const dragRef = useRef<{ startClientX: number; startClientY: number; originX: number; originY: number } | null>(null);

    const isSealed = sealedLocally || alreadyWritten;
    const canWrite = !isSealed && isWriting;

    useEffect(() => {
        setText(card.text || '');
        if (hasWrittenText(card.text)) {
            setSealedLocally(true);
            setIsWriting(false);
        }
    }, [card.text, card.id]);

    useEffect(() => {
        if (startEditing && !hasWrittenText(card.text) && !sealedLocally) {
            setIsWriting(true);
        }
    }, [startEditing, card.id, card.text, sealedLocally]);

    useEffect(() => {
        if (canWrite && textareaRef.current) {
            textareaRef.current.focus();
        }
    }, [canWrite, card.id]);

    const handlePointerDown = useCallback(
        (e: React.PointerEvent<HTMLDivElement>) => {
            if ((e.target as HTMLElement).closest('textarea,button,.retro-sticky-card__masked')) {
                return;
            }
            e.preventDefault();
            e.stopPropagation();
            dragRef.current = {
                startClientX: e.clientX,
                startClientY: e.clientY,
                originX: card.x,
                originY: card.y,
            };
            (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
        },
        [card.x, card.y]
    );

    const handlePointerMove = useCallback(
        (e: React.PointerEvent<HTMLDivElement>) => {
            if (!dragRef.current) return;
            e.stopPropagation();
            const surface = surfaceRef.current;
            if (!surface) return;

            const rect = surface.getBoundingClientRect();
            if (rect.width <= 0 || rect.height <= 0) return;

            const dx = (e.clientX - dragRef.current.startClientX) / rect.width;
            const dy = (e.clientY - dragRef.current.startClientY) / rect.height;

            const x = clamp(dragRef.current.originX + dx, 0, 0.85);
            const y = clamp(dragRef.current.originY + dy, 0, 0.85);
            onMove(card.id, x, y);
        },
        [card.id, onMove, surfaceRef]
    );

    const handlePointerUp = useCallback((e: React.PointerEvent<HTMLDivElement>) => {
        if (dragRef.current) {
            e.stopPropagation();
            dragRef.current = null;
            try {
                (e.currentTarget as HTMLElement).releasePointerCapture(e.pointerId);
            } catch {
                /* ignore */
            }
        }
    }, []);

    const commitText = useCallback(() => {
        if (!canWrite) return;

        const next = text.trim();
        if (next !== (card.text || '').trim()) {
            onTextChange(card.id, text);
        }

        // İlk kayıttan sonra tekrar edit yok (boş bile olsa oturum kapanır)
        setIsWriting(false);
        setSealedLocally(true);
        onEditFinished?.(card.id);
    }, [canWrite, card.id, card.text, onEditFinished, onTextChange, text]);

    const showMasked = contentHidden && !canWrite;
    const maskedText = maskText(card.text || text);

    return (
        <div
            className={`retro-sticky-card ${showMasked ? 'retro-sticky-card--hidden' : ''} ${isSealed ? 'retro-sticky-card--locked' : ''}`}
            style={{
                left: `${card.x * 100}%`,
                top: `${card.y * 100}%`,
                backgroundColor: card.color || '#fef08a',
            }}
            onPointerDown={handlePointerDown}
            onPointerMove={handlePointerMove}
            onPointerUp={handlePointerUp}
            onPointerCancel={handlePointerUp}
            onMouseMove={(e) => e.stopPropagation()}
        >
            <div className="retro-sticky-card__header">
                {!contentHidden && (
                    <button
                        type="button"
                        className="retro-sticky-card__delete"
                        title={t('retroBoard.deleteCard')}
                        onClick={(e) => {
                            e.stopPropagation();
                            onDelete(card.id);
                        }}
                    >
                        ×
                    </button>
                )}
            </div>
            {showMasked ? (
                <div className="retro-sticky-card__masked" aria-hidden>
                    {maskedText || '****'}
                </div>
            ) : (
                <textarea
                    ref={textareaRef}
                    className="retro-sticky-card__text"
                    value={canWrite ? text : card.text || text}
                    placeholder={t('retroBoard.cardPlaceholder')}
                    maxLength={500}
                    readOnly={!canWrite}
                    onChange={(e) => {
                        if (!canWrite) return;
                        setText(e.target.value);
                    }}
                    onBlur={commitText}
                    onPointerDown={(e) => e.stopPropagation()}
                    onClick={(e) => e.stopPropagation()}
                />
            )}
        </div>
    );
};

function maskText(value: string): string {
    if (!value) return '';
    return [...value].map((ch) => (ch === '\n' || ch === '\r' ? ch : '*')).join('');
}

function clamp(value: number, min: number, max: number): number {
    if (value < min) return min;
    if (value > max) return max;
    return value;
}
